import { LogService } from '@absg/angular-common';
import { AlertBannerSummaryComponent, AlertSummaryComponent, ConfirmModalService, UxInfoService } from '@absg/angular-components';
import { AuthenticationService, AuthorizationService, ConfigurationService } from '@absg/angular-platform';
import { E2eService, EnvironmentService, NimAlertService, SessionService } from '@absg/nim-client-shared';
import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { Environments } from 'core.enums/environments';
import { AccessPointService } from 'core.services/access-point/access-point.service';
import { LockAccessService } from 'core.services/access-point/lock-access.service';
import { StationAuthenticationService } from 'core.services/station-authentication.service';
import { StationInfoService } from 'core.services/station-info.service';
import { BehaviorSubject } from 'rxjs';
import { map, skipWhile, take, takeWhile } from 'rxjs/operators';
import { NIMGoogleTagMangerService } from 'shared.root/services/gtm-tag.service';
import { SubSink } from 'subsink';

@Component({
  selector: '[nc-main]',
  templateUrl: './main.component.html',
  standalone: false,
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  fullScreen = false;
  isSpinnerVisible = true;
  isScannerOn = false;
  showAccessDenied: boolean;
  private subSink = new SubSink();
  @ViewChild(AlertBannerSummaryComponent) alertBannerSummaryComponent: AlertBannerSummaryComponent;
  @ViewChild(AlertSummaryComponent) alertSummaryComponent: AlertSummaryComponent;

  private isSwitchStationOpen = new BehaviorSubject<boolean>(false);
  private isBarcodeSimulatorOpen = new BehaviorSubject<boolean>(false);
  private devPrintOpenAction = new BehaviorSubject<boolean>(false);
  switchStationOpenAction$ = this.isSwitchStationOpen.asObservable();
  barcodeSimulatorOpenAction$ = this.isBarcodeSimulatorOpen.asObservable();
  openAccessDoorModal$ = new BehaviorSubject<boolean>(false);
  showWarningMsg$ = new BehaviorSubject<boolean>(true);
  devPrintOpenAction$ = this.devPrintOpenAction.asObservable();

  isDevOrTest: boolean;
  isProd: boolean;
  practiceIdForBarcodeSimulator = 0;

  constructor(
    confirmModalService: ConfirmModalService, // bring back?
    uxInfoService: UxInfoService,
    private router: Router,
    private environmentService: EnvironmentService,
    private stationInfoService: StationInfoService,
    private loggingService: LogService,
    private sessionService: SessionService,
    private hotkeysService: HotkeysService,
    private nimAlertService: NimAlertService,
    private configurationService: ConfigurationService,
    private accessPointService: AccessPointService,
    private e2eService: E2eService,
    private gtmService: GoogleTagManagerService,
    private gtmNimService: NIMGoogleTagMangerService,
    authenticationService: AuthenticationService,
    private stationAuthenticationService: StationAuthenticationService,
    private zone: NgZone,
    private lockAccessService: LockAccessService,
    authorizationService: AuthorizationService,
  ) {
    this.subSink.sink = authorizationService.listenForUserChanges$.subscribe();
    this.subSink.sink = authenticationService.msal$.subscribe();
    this.subSink.sink = this.stationInfoService.getAccessDenied().subscribe((isAccessDenied: boolean) => this.showAccessDenied = isAccessDenied);
    this.subSink.sink = router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (event.url === '/password-reset') {
          this.fullScreen = true;
        } else {
          this.fullScreen = false;
        }
        // console.log(event.url);
      }
    });

    this.subSink.sink = stationAuthenticationService.dismissModalSubject.subscribe(() => {
      confirmModalService.dismissCurrentModals();
    });
    // tell angular components that any shared component should show for station, not web
    // currently limited to alert confirmation modals
    uxInfoService.isStation = true;

    this.subSink.sink = stationInfoService.loadingData.subscribe(loading => this.isSpinnerVisible = loading);
    this.setShortCutKeys();
  }

  isAccessDeniedOkClicked(): void {
    this.showAccessDenied = false;
    this.stationAuthenticationService.redirectToLogoutPage().pipe(take(1)).subscribe();
  }

  ngOnInit(): void {
    this.stationInfoService.requestHostName();
    this.subSink.sink = this.sessionService.practiceDomain
      .pipe(takeWhile(() => this.isSpinnerVisible))
      .subscribe((domain) => {
        if (domain != null) {
          setTimeout(() => {
            this.isSpinnerVisible = false;
          });
        }
      });

    this.isDevOrTest = this.environmentIsDevOrTest();
    this.isProd = this.environmentService.isProduction();

    this.subSink.sink = this.nimAlertService.shutDownAlerts.subscribe((value) => {
      if (value) {
        this.closeAllAlerts();
      }
    });

    this.subSink.sink = this.nimAlertService.isModalAlert.subscribe((value) => {
      if (this.alertBannerSummaryComponent) {
        this.alertBannerSummaryComponent.setModalAlerts(value);
      }
    });

    this.subSink.sink = this.sessionService.shouldCloseModals$.subscribe((value) => {
      if (value) {
        this.resetAccessDoorModal();
        this.onBarcodeSimulatorClose();
      }
    });
    if (window.location.href.includes('app.asar')) {
      this.subSink.sink = this.router.events.subscribe(
        (item) => {
          if (item instanceof NavigationEnd) {
            this.gtmNimService.checkForGoogleTagItems();
          }
        });

      this.zone.runOutsideAngular(() => {
        document.addEventListener('click', () => {
          this.gtmNimService.checkForClickEvents();
        }, false);
      });
    }

    this.subSink.sink = this.accessPointService.isDoorFailure.subscribe((isFailure) => {
      if (isFailure && this.stationInfoService.getIsLoggedIn() && this.nimAlertService.isResolveDoorPromptMessageOpen.value === false) {
        const title = 'Failed Door Alert';
        const message = 'Click "Resolve" to clear the failed door(s).';
        const yesButtonText = 'Resolve';
        this.nimAlertService.showResolveDoorPrompt(title, message, 'Open-Door', 'maroon', true, false, yesButtonText)
          .pipe(take(1))
          .subscribe(() => {
            this.accessPointService.isResolvedDoorOpen.next(true);
          });
      }
    });
    this.subSink.sink = this.environmentService.scannerEnabled.pipe(map((value) => {
      this.isScannerOn = value;
    })).subscribe();
  }

  ngAfterViewInit(): void {
    this.showWarningMsg();
    this.gtmService.addGtmToDom();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
    // Ensure that logging buffer is flushed when application closes
    this.loggingService.flush(false);
  }

  showWarningMsg() {
    this.configurationService.loginSettingsLoaded$
      .asObservable()
      .pipe(
        skipWhile(value => !value),
        take(1))
      .subscribe(() => {
        const maxAllowedThresholdInSeconds = 60; //secs
        if (this.configurationService.getEnvironment().serverClientTimeDiffInSeconds > maxAllowedThresholdInSeconds) {
          this.nimAlertService.showErrorAlert(`The time on your workstation is different than the server clock which is
            preventing login. Please notify your IT admin or OSC Help Desk to get this resolved.`);
          this.showWarningMsg$.next(true);
        } else {
          this.showWarningMsg$.next(false);
        }
      });
  }

  setShortCutKeys(): void {
    if (!this.environmentService.isNotTest()) {
      this.hotkeysService.add(new Hotkey(['ctrl+h', 'meta+h'], (event: KeyboardEvent): boolean => {
        console.log('Typed hotkey');
        this.router.navigate(['/home']);
        event.preventDefault();
        return false; // Prevent bubbling
      }));
    }
    if (!this.environmentService.isProduction()) {
      this.hotkeysService.add(new Hotkey(['ctrl+shift+t'], (event: KeyboardEvent): boolean => {
        this.openSwitchStation();
        event.preventDefault();
        return false; // Prevent bubbling
      }));
      this.hotkeysService.add(new Hotkey(['ctrl+alt+t'], (event: KeyboardEvent): boolean => {
        this.openSwitchStation();
        event.preventDefault();
        return false; // Prevent bubbling
      }));
    }
    if (!this.environmentService.isProduction()) {
      this.hotkeysService.add(new Hotkey(['ctrl+shift+u'], (event: KeyboardEvent): boolean => {
        this.openBarcodeSimulator();
        event.preventDefault();
        return false; // Prevent bubbling
      }, ['INPUT']));
    }
    if (!this.environmentService.isProduction()) {
      this.hotkeysService.add(new Hotkey(['ctrl+shift+j'], (event: KeyboardEvent): boolean => {
        this.openAccessPointModal();
        event.preventDefault();
        return false; // Prevent bubbling
      }));
    }
    if (!this.environmentService.isProduction()) {
      this.hotkeysService.add(new Hotkey(['ctrl+shift+p'], (event: KeyboardEvent): boolean => {
        this.devPrintOpenAction.next(true);
        event.preventDefault();
        return false; // Prevent bubbling
      }));
    }
  }

  scanDetected(event): void {
    this.nimAlertService.showSuccessAlert(event);
  }

  closeAllAlerts(): void {
    this.accessPointService.isResolvedDoorOpen.next(false);
    this.alertSummaryComponent.closeAll();
  }

  onSwitchStationClose(): void {
    this.isSwitchStationOpen.next(false);
  }

  onDevPrintClose(): void {
    this.devPrintOpenAction.next(false);
  }

  onBarcodeSimulatorClose(): void {
    this.isBarcodeSimulatorOpen.next(false);
  }

  resetAccessDoorModal(): void {
    this.openAccessDoorModal$.next(false);
  }

  openSwitchStation(): void {
    if (!this.stationInfoService.getIsLoggedIn()) {
      this.isSwitchStationOpen.next(true);
    }
  }

  openBarcodeSimulator(): void {
    if (this.stationInfoService.getIsLoggedIn()) {
      this.practiceIdForBarcodeSimulator = this.stationInfoService.practiceClinicDetail.getValue()?.practiceId;
      this.isBarcodeSimulatorOpen.next(true);
    }
  }

  openAccessPointModal(): void {
    if (this.stationInfoService.getIsLoggedIn()) {
      this.openAccessDoorModal$.next(true);
    }
  }

  onBtnSwitchStation(): void {
    this.openSwitchStation();
  }

  onBtnBarcodeScan(): void {
    this.openBarcodeSimulator();
  }

  environmentIsDevOrTest(): boolean {
    if (this.environmentService.currentEnvironment === Environments.dev
      || this.environmentService.currentEnvironment === Environments.local
      || this.environmentService.currentEnvironment === Environments.test) {
      return true;
    }
    return false;
  }

  extendSuccessAlertTimeout(): void {
    this.nimAlertService.extendSuccessAlertTimeout(60000);
    // set to capture printing, so call printing service to be able to get that data
    this.e2eService.setE2eMode(true);
    this.lockAccessService.unlockRequestOpenDoorDelay = 50;
    this.lockAccessService.unlockRequestCloseDoorDelay = 200;
  }

  enableBarcodeScan(): void {
    this.environmentService.toggleScanner(true);
  }
}
